import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  FileText, 
  Search, 
  Filter, 
  Download, 
  Calendar, 
  User,
  Database
} from 'lucide-react';
import { format } from 'date-fns';

interface ExportLog {
  id: string;
  export_type: string;
  module_source: string;
  filename: string | null;
  user_email: string | null;
  project_id: string | null;
  status: string | null;
  exported_at: string;
  created_at: string;
  notes: string | null;
}

export default function ExportAuditScreen() {
  const [searchTerm, setSearchTerm] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch export audit logs
  const { data: logs = [], isLoading } = useQuery<ExportLog[]>({
    queryKey: ['/api/admin/export-audit'],
    staleTime: 30 * 1000, // 30 seconds
  });

  // Filter logs based on search and filters
  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchTerm || 
      log.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.project_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesModule = moduleFilter === 'all' || log.module_source === moduleFilter;
    const matchesStatus = statusFilter === 'all' || log.status === statusFilter;
    
    return matchesSearch && matchesModule && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-400">Completed</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-400">Failed</Badge>;
      case 'processing':
        return <Badge className="bg-yellow-500/20 text-yellow-400">Processing</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  };

  return (
    <div className="min-h-screen bg-forestBlack text-white font-['Archivo',sans-serif]">
      <div className="container mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2 flex items-center gap-3">
            <Database className="w-8 h-8" />
            Export Audit Trail
          </h1>
          <p className="text-gray-300">
            Monitor and audit all PDF and data exports across the platform
          </p>
        </div>

        {/* Filters */}
        <Card className="bg-[#0A2A35] border-primary/20 mb-6">
          <CardHeader>
            <CardTitle className="text-primary flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by filename, user, or project..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-forestBlack border-primary/30 text-white"
                />
              </div>
              
              <Select value={moduleFilter} onValueChange={setModuleFilter}>
                <SelectTrigger className="bg-forestBlack border-primary/30">
                  <SelectValue placeholder="All Modules" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A2A35] border-primary/20">
                  <SelectItem value="all">All Modules</SelectItem>
                  <SelectItem value="maturity">Maturity Assessment</SelectItem>
                  <SelectItem value="roi">ROI Analysis</SelectItem>
                  <SelectItem value="value_engineering">Value Engineering</SelectItem>
                  <SelectItem value="benchmarking">Benchmarking</SelectItem>
                  <SelectItem value="style_guide">Style Guide</SelectItem>
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-forestBlack border-primary/30">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A2A35] border-primary/20">
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Export Logs Table */}
        <Card className="bg-[#0A2A35] border-primary/20">
          <CardHeader>
            <CardTitle className="text-primary flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Export History ({filteredLogs.length} records)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-gray-400">Loading export logs...</div>
            ) : filteredLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No export logs found matching your criteria
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-primary/20">
                      <TableHead className="text-primary">Filename</TableHead>
                      <TableHead className="text-primary">Type</TableHead>
                      <TableHead className="text-primary">Module</TableHead>
                      <TableHead className="text-primary">User</TableHead>
                      <TableHead className="text-primary">Status</TableHead>
                      <TableHead className="text-primary">Exported</TableHead>
                      <TableHead className="text-primary">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLogs.map((log) => (
                      <TableRow key={log.id} className="border-primary/20">
                        <TableCell className="text-white">
                          <div className="flex items-center gap-2">
                            <Download className="w-4 h-4 text-primary" />
                            <span className="font-mono text-sm">{log.filename || 'N/A'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-primary/30 text-primary">
                            {log.export_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-primary/30 text-primary">
                            {log.module_source}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-300">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            {log.user_email || 'Unknown'}
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(log.status || 'unknown')}
                        </TableCell>
                        <TableCell className="text-gray-300">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {formatDate(log.exported_at)}
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-300 text-sm">
                          {log.notes || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}